import { createSelector } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { filterNotDeleted } from "../../../shared/api/filter-not-deleted";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import { TechOperationGroupCode } from "../shared/enums/tech-operation-group-code";

export interface ITechOperationGroupsDto {
  id: string;
  name: string;
  code: TechOperationGroupCode;
  parentId: string | null;
  importId: string;
  deletedAt: number | null;
  isDeleted: boolean;
  createdAt: number | null;
  updatedAt: number | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
}

export const techOperationGroupsApi = createApi({
  reducerPath: "techOperationGroupsApi",
  baseQuery,
  endpoints: (builder) => ({
    fetchTechOperationGroupsWithDeleted: builder.query<ITechOperationGroupsDto[], void>({
      queryFn: pagedQueryFn<ITechOperationGroupsDto, ITechOperationGroupsDto[]>(
        { url: "TechOperationGroupsV2", params: { includeDeleted: true } },
        (dtos) => dtos
      ),
    }),
  }),
});

const selectNotDeletedData = createSelector(
  ({ data }: { data?: ITechOperationGroupsDto[] }) => data,
  filterNotDeleted<ITechOperationGroupsDto>
);

const selectNotDeletedCurrentData = createSelector(
  ({ currentData }: { currentData?: ITechOperationGroupsDto[] }) => currentData,
  filterNotDeleted<ITechOperationGroupsDto>
);

const useTechOperationGroupsEndpoint =
  techOperationGroupsApi.endpoints.fetchTechOperationGroupsWithDeleted.useQuery;

type TechOperationGroupsQueryOptions = QuerySubscriptionOptions<
  typeof useTechOperationGroupsEndpoint
>;

export function useFetchTechOperationGroups(options?: TechOperationGroupsQueryOptions) {
  return useTechOperationGroupsEndpoint(undefined, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: selectNotDeletedData(result),
      currentData: selectNotDeletedCurrentData(result),
    }),
  });
}

export function useFetchTechOperationGroupsWithDeleted(
  options?: TechOperationGroupsQueryOptions
) {
  return useTechOperationGroupsEndpoint(undefined, options);
}
